<template>
  <v-row class="ma-0 pa-0 psych-overview-scatter" justify="start">
    <v-col cols="12" class="ma-0 pa-0">
      <v-chart class="chart" :option="graph_options" autoresize></v-chart>
    </v-col>
  </v-row>
</template>

<script>
import VChart, { THEME_KEY } from "vue-echarts";
import "echarts";
import { use } from "echarts/core";
import { ScatterChart } from "echarts/charts";
import { CanvasRenderer } from "echarts/renderers";
import {
  GridComponent,
  LegendComponent,
  MarkAreaComponent,
  MarkLineComponent,
  TitleComponent,
  TooltipComponent,
} from "echarts/components";

use([
  ScatterChart,
  CanvasRenderer,
  GridComponent,
  LegendComponent,
  MarkAreaComponent,
  MarkLineComponent,
  TitleComponent,
  TooltipComponent,
]);

export default {
  name: "PsychOverviewScatter",
  props: ["chart_data"],
  provide: {
    [THEME_KEY]: "light",
  },
  components: {
    VChart,
  },
  computed: {
    chartColors: function () {
      return [
        "#D71920",
        "#0A8C4E",
        "#2F6BFF",
        "#F2A900",
        "#7A3E9D",
        "#00A6A6",
        "#E4572E",
        "#5C677D",
        "#8CB369",
        "#C44569",
        "#1B998B",
        "#6A4C93",
      ];
    },
    scatterPoints: function () {
      return (this.chart_data || [])
        .map((row) => {
          const xValue = Number(row[2]);
          const yValue = Number(row[1]);

          if (!Number.isFinite(xValue) || !Number.isFinite(yValue)) {
            return null;
          }

          return {
            name: row[0],
            value: [xValue, yValue],
          };
        })
        .filter(Boolean);
    },
    xExtent: function () {
      return this.getAxisExtent(0, this.xAverage);
    },
    yExtent: function () {
      return this.getAxisExtent(1, this.yAverage);
    },
    xAverage: function () {
      if (this.scatterPoints.length === 0) {
        return 0;
      }

      return (
        this.scatterPoints.reduce((total, point) => total + point.value[0], 0) /
        this.scatterPoints.length
      );
    },
    yAverage: function () {
      if (this.scatterPoints.length === 0) {
        return 0;
      }

      return (
        this.scatterPoints.reduce((total, point) => total + point.value[1], 0) /
        this.scatterPoints.length
      );
    },
    quadrantMarkAreas: function () {
      return [
        [
          {
            name: "General Trends",
            coord: [this.xExtent.min, this.yAverage],
            label: {
              position: "insideTopLeft",
              padding: [12, 0, 0, 12],
            },
          },
          {
            coord: [this.xAverage, this.yExtent.max],
          },
        ],
        [
          {
            name: "Most Popular Trends",
            coord: [this.xAverage, this.yAverage],
            label: {
              position: "insideTopRight",
              padding: [12, 12, 0, 0],
            },
          },
          {
            coord: [this.xExtent.max, this.yExtent.max],
          },
        ],
        [
          {
            name: "Least Popular Trends",
            coord: [this.xExtent.min, this.yExtent.min],
            label: {
              position: "insideBottomLeft",
              padding: [0, 0, 12, 12],
            },
          },
          {
            coord: [this.xAverage, this.yAverage],
          },
        ],
        [
          {
            name: "Niche Trends",
            coord: [this.xAverage, this.yExtent.min],
            label: {
              position: "insideBottomRight",
              padding: [0, 12, 12, 0],
            },
          },
          {
            coord: [this.xExtent.max, this.yAverage],
          },
        ],
      ];
    },
    pointSeries: function () {
      return this.scatterPoints.map((point, index) => ({
        name: point.name,
        type: "scatter",
        data: [point],
        symbolSize: 14,
        itemStyle: {
          color: this.chartColors[index % this.chartColors.length],
        },
        markArea:
          index === 0
            ? {
                silent: true,
                itemStyle: {
                  color: "transparent",
                },
                label: {
                  color: "#2B2B2B",
                  fontFamily: "Poppins",
                  fontSize: 12,
                  fontWeight: 600,
                },
                emphasis: {
                  disabled: true,
                },
                data: this.quadrantMarkAreas,
              }
            : undefined,
        markLine:
          index === 0
            ? {
                silent: true,
                symbol: "none",
                lineStyle: {
                  color: "#000000",
                  type: "dashed",
                  width: 2,
                },
                label: {
                  show: false,
                },
                data: [
                  { xAxis: this.xAverage },
                  { yAxis: this.yAverage },
                ],
              }
            : undefined,
      }));
    },
    graph_options: function () {
      return {
        title: {
          text: "Psych Index vs Size",
          left: "center",
          top: 0,
          textStyle: {
            fontFamily: "Poppins",
            fontSize: 16,
            fontWeight: 600,
          },
        },
        color: this.chartColors,
        legend: {
          type: "scroll",
          orient: "vertical",
          align: "left",
          left: "92%",
          right: 0,
          top: 64,
          bottom: 48,
          width: "8%",
          itemGap: 10,
          textStyle: {
            align: "left",
            fontFamily: "Poppins",
            fontSize: 11,
            overflow: "truncate",
            width: 140,
          },
        },
        tooltip: {
          trigger: "item",
          textStyle: {
            fontFamily: "Poppins",
          },
          formatter: function (param) {
            const value = param.value || [];
            return [
              param.name,
              `Psych Index: ${Number(value[0] || 0).toLocaleString()}`,
              `Size: ${Number(value[1] || 0).toLocaleString()}`,
            ].join("<br/>");
          },
        },
        grid: {
          left: 72,
          right: "10%",
          top: 64,
          bottom: 56,
          containLabel: true,
        },
        xAxis: {
          type: "value",
          name: "Psych Index",
          min: this.xExtent.min,
          max: this.xExtent.max,
          nameLocation: "middle",
          nameGap: 34,
          axisLabel: {
            fontFamily: "Poppins",
            fontSize: 11,
          },
          splitLine: {
            show: true,
          },
        },
        yAxis: {
          type: "value",
          name: "Size",
          min: this.yExtent.min,
          max: this.yExtent.max,
          nameLocation: "middle",
          nameGap: 54,
          axisLabel: {
            fontFamily: "Poppins",
            fontSize: 11,
            formatter: function (value) {
              return Intl.NumberFormat("en", {
                notation: "compact",
                maximumFractionDigits: 1,
              }).format(value);
            },
          },
          splitLine: {
            show: true,
          },
        },
        series: this.pointSeries,
      };
    },
  },
  methods: {
    getAxisExtent: function (valueIndex, average) {
      const values = this.scatterPoints
        .map((point) => point.value[valueIndex])
        .filter((value) => Number.isFinite(value));

      if (values.length === 0) {
        return { min: 0, max: 1 };
      }

      const minValue = 0;
      const maxValue = Math.max(...values, average);
      const range = Math.max(maxValue - minValue, 1);
      const padding = range > 0 ? range * 0.12 : Math.max(Math.abs(maxValue) * 0.12, 1);

      return {
        min: minValue,
        max: maxValue + padding,
      };
    },
  },
};
</script>

<style scoped>
.psych-overview-scatter {
  height: 70vh;
  width: 100%;
}

.chart {
  height: 100%;
  width: 100%;
}
</style>
